// pages/LeadStatusPage.jsx
//
// Renders a filtered, paginated list of leads for a given status tab.
// Route: /dashboard/lead-status/:tabValue
//
// Uses GET /leads?status=<comma-separated statuses>&search=...&page=...
// so filtering is fully server-side — no client-side re-filtering needed.

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';
import { ArrowLeftIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { Button }     from '../components/ui/button';
import { Input }      from '../components/ui/input';
import { Badge }      from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { LEAD_TABS, getTabConfig, getStatusQueryParam } from '../lib/leadTabs';

// ─── Constants ────────────────────────────────────────────────────────────────
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PAGE_SIZE    = 20;

const getToken  = () => localStorage.getItem('admin_token');
const withAuth  = (opts = {}) => ({
  ...opts,
  headers: {
    ...(opts.headers || {}),
    ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    ...(opts.body && !opts.headers?.['Content-Type'] ? { 'Content-Type': 'application/json' } : {}),
  },
});

// ─── Status badge colours (same map as LeadDetailsDialog) ────────────────────
const STATUS_COLORS = {
  new:                  'bg-sky-100 text-sky-800',
  inprogress:           'bg-blue-100 text-blue-800',
  attended:             'bg-indigo-100 text-indigo-800',
  interested:           'bg-violet-100 text-violet-800',
  not_interested:       'bg-red-100 text-red-800',
  enrolled_with_others: 'bg-orange-100 text-orange-800',
  not_responding:       'bg-yellow-100 text-yellow-800',
  enrolled:             'bg-emerald-100 text-emerald-800',
  // legacy
  open:              'bg-blue-100 text-blue-800',
  contacted:         'bg-yellow-100 text-yellow-800',
  demo_scheduled:    'bg-indigo-100 text-indigo-800',
  awaiting_decision: 'bg-purple-100 text-purple-800',
  converted:         'bg-green-100 text-green-800',
  lost:              'bg-gray-100 text-gray-800',
};

const STATUS_LABELS = {
  new:                  'New',
  inprogress:           'In Progress',
  attended:             'Attended',
  interested:           'Interested',
  not_interested:       'Not Interested',
  enrolled_with_others: 'Enrolled with Others',
  not_responding:       'Not Responding',
  enrolled:             'Enrolled',
  open:                 'Open',
  contacted:            'Contacted',
  demo_scheduled:       'Demo Scheduled',
  awaiting_decision:    'Awaiting Decision',
  converted:            'Converted',
  lost:                 'Lost',
};

const PRIORITY_COLORS = {
  high:   'bg-red-100 text-red-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low:    'bg-green-100 text-green-800',
};

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const formatDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// ─── Row skeleton ─────────────────────────────────────────────────────────────
const RowSkeleton = () => (
  <TableRow>
    {[1, 2, 3, 4, 5, 6].map(i => (
      <TableCell key={i}>
        <div className="h-4 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse w-24" />
      </TableCell>
    ))}
  </TableRow>
);

// ─────────────────────────────────────────────────────────────────────────────

export default function LeadStatusPage() {
  usePageMeta(PAGE_META.leadStatus);
  const { tabValue }  = useParams();
  const navigate      = useNavigate();
  const tabConfig     = getTabConfig(tabValue);   // falls back to first tab if unknown

  const [leads,    setLeads]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState('');
  const [priority, setPriority] = useState('');   // '' = all
  const [loading,  setLoading]  = useState(false);

  // ── Fetch leads from API ───────────────────────────────────────────────────
  // `opts.tab` lets callers pass the tab value explicitly so there's never a
  // stale-closure issue when the URL param changes between renders.
  const fetchLeads = useCallback(async (opts = {}) => {
    const currentTab      = opts.tab      ?? tabValue;   // ← always use the caller's value
    const currentPage     = opts.page     ?? page;
    const currentSearch   = opts.search   ?? search;
    const currentPriority = opts.priority ?? priority;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('status',    getStatusQueryParam(currentTab));  // ← no stale closure
      params.set('page',      currentPage);
      params.set('limit',     PAGE_SIZE);
      params.set('sortBy',    'createdAt');
      params.set('sortOrder', 'desc');
      if (currentSearch)   params.set('search',   currentSearch);
      if (currentPriority) params.set('priority', currentPriority);

      const res  = await fetch(`${API_BASE_URL}/leads?${params}`, withAuth());
      if (!res.ok) throw new Error('Failed to fetch leads');
      const json = await res.json();

      setLeads(json.data || []);
      setTotal(json.pagination?.total || 0);
      setPages(json.pagination?.pages || 1);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [tabValue, page, search, priority]);

  // Re-fetch whenever the tab in the URL changes — pass tabValue explicitly
  // so fetchLeads never reads a stale closure value.
  useEffect(() => {
    setPage(1);
    setSearch('');
    setPriority('');
    fetchLeads({ tab: tabValue, page: 1, search: '', priority: '' });
  }, [tabValue]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSearch = (value) => {
    setSearch(value);
    if (value.length === 0 || value.length >= 3) {
      setPage(1);
      fetchLeads({ search: value, page: 1 });
    }
  };

  const handlePriority = (value) => {
    const v = value === 'all' ? '' : value;
    setPriority(v);
    setPage(1);
    fetchLeads({ priority: v, page: 1 });
  };

  const handlePage = (next) => {
    setPage(next);
    fetchLeads({ page: next });
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4 p-4 text-[13px] max-w-full">

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-1.5">
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div className={`p-2 rounded-full bg-gradient-to-br ${tabConfig.color}`}>
          <FunnelIcon className="h-4 w-4 text-white" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">{tabConfig.label}</h1>
          <p className="text-xs text-gray-500">
            {total} lead{total !== 1 ? 's' : ''} matching this status
          </p>
        </div>
      </div>

      {/* Tab quick-switch */}
      <div className="flex flex-wrap gap-2">
        {LEAD_TABS.map(tab => (
          <Button
            key={tab.value}
            size="sm"
            variant={tab.value === tabConfig.value ? 'default' : 'outline'}
            className={`text-xs rounded-md ${tab.value === tabConfig.value ? `bg-gradient-to-r ${tab.color} text-white border-0` : ''}`}
            onClick={() => navigate(`/dashboard/lead-status/${tab.value}`)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, email or phone…"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8 text-sm h-9"
            />
          </div>
          {/* Priority filter */}
          <Select value={priority || 'all'} onValueChange={handlePriority}>
            <SelectTrigger className="w-full sm:w-36 h-9 text-sm">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all"    className="text-sm">All Priorities</SelectItem>
              <SelectItem value="high"   className="text-sm">High</SelectItem>
              <SelectItem value="medium" className="text-sm">Medium</SelectItem>
              <SelectItem value="low"    className="text-sm">Low</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm">
            {tabConfig.label} Leads
          </CardTitle>
          <CardDescription className="text-xs">
            Showing page {page} of {pages} — {total} total
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wide pl-4 text-white">Name</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-white">Contact</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-white">Course</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-white">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-white">Priority</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-white">Follow-up</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-white">Owner</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide pr-4 text-white">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} />)
                ) : leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-gray-500 text-sm">
                      No leads found for <span className="font-medium">{tabConfig.label}</span>
                      {search && <> matching "<span className="font-medium">{search}</span>"</>}
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead) => (
                    <TableRow
                      key={lead._id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/crm?lead=${lead._id}`)}
                    >
                      {/* Name */}
                      <TableCell className="pl-4 py-3">
                        <p className="font-medium text-gray-900 text-sm">{lead.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{lead.source || '—'}</p>
                      </TableCell>

                      {/* Contact */}
                      <TableCell className="py-3">
                        <p className="text-xs text-gray-700">{lead.phone}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[140px]">{lead.email || '—'}</p>
                      </TableCell>

                      {/* Course */}
                      <TableCell className="text-xs text-gray-700 py-3">
                        {lead.interestCourse?.name || '—'}
                      </TableCell>

                      {/* Status badge */}
                      <TableCell className="py-3">
                        <Badge className={`text-xs ${STATUS_COLORS[lead.status] ?? 'bg-gray-100 text-gray-800'}`}>
                          {STATUS_LABELS[lead.status] ?? lead.status}
                        </Badge>
                      </TableCell>

                      {/* Priority badge */}
                      <TableCell className="py-3">
                        <Badge className={`text-xs capitalize ${PRIORITY_COLORS[lead.priority] ?? 'bg-gray-100 text-gray-800'}`}>
                          {lead.priority}
                        </Badge>
                      </TableCell>

                      {/* Follow-up date */}
                      <TableCell className="text-xs text-gray-700 py-3 whitespace-nowrap">
                        {lead.followUpDate
                          ? <span className={new Date(lead.followUpDate) < new Date() ? 'text-red-600 font-medium' : ''}>
                              {formatDateTime(lead.followUpDate)}
                            </span>
                          : <span className="text-gray-400">Not set</span>
                        }
                      </TableCell>

                      {/* Owner */}
                      <TableCell className="text-xs text-gray-700 py-3">
                        {lead.owner?.name || 'System'}
                      </TableCell>

                      {/* Created date */}
                      <TableCell className="text-xs text-gray-500 pr-4 py-3 whitespace-nowrap">
                        {formatDate(lead.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} leads
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1 || loading}
              onClick={() => handlePage(page - 1)}
              className="text-xs"
            >
              ← Previous
            </Button>
            {/* Page number pills */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                const p = pages <= 7 ? i + 1
                  : page <= 4 ? i + 1
                  : page >= pages - 3 ? pages - 6 + i
                  : page - 3 + i;
                return (
                  <Button
                    key={p}
                    size="sm"
                    variant={p === page ? 'default' : 'outline'}
                    onClick={() => handlePage(p)}
                    className={`text-xs w-8 ${p === page ? `bg-gradient-to-r ${tabConfig.color} text-white border-0` : ''}`}
                  >
                    {p}
                  </Button>
                );
              })}
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= pages || loading}
              onClick={() => handlePage(page + 1)}
              className="text-xs"
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}